/**
 * Keyboard Actions — PC sticky pin + scrub.
 * Product starts vertically centered; scrub across full track to endTop (-340*scale).
 * Track 300svh. Dwell 15% at start + 15% at end (hold final frame), scrub in between.
 * Scale fits end-frame: (540+64)*scale + intro + chips + pad ≤ available.
 * Mobile (<1024): no animation. Reduced-motion: end state.
 */
(function () {
  'use strict';

  var LG = 1024;
  var BOTTOM_PAD = 48;
  var PRODUCT_BASE = 800;
  var END_TOP_BASE = -340;
  /* Pin end-frame positions that shrink with --kb-actions-scale */
  var REVEAL_TOP_BASE = 540;
  var CHIPS_MARGIN_BASE = 64;
  var SCALED_PART = REVEAL_TOP_BASE + CHIPS_MARGIN_BASE;

  function clamp(n, a, b) {
    return Math.min(b, Math.max(a, n));
  }

  function lerp(a, b, t) {
    return a + (b - a) * t;
  }

  /* cubic-bezier(0.5, 0, 0.5, 1) approx via easeInOut midpoint */
  function easeMid(t) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  function kvBarH() {
    var bar = document.querySelector('[data-lp-kv-bar].is-fixed');
    if (!bar) return 0;
    return Math.round(bar.getBoundingClientRect().height);
  }

  class KeyboardActions extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this._track = this.querySelector('[data-kb-actions-track]');
      this._stage = this.querySelector('[data-kb-actions-stage]');
      this._product = this.querySelector('[data-kb-actions-product]');
      this._reveal = this.querySelector('[data-kb-actions-reveal]');
      this._intro = this.querySelector('.kb-actions__intro');
      this._chips = this.querySelector('.kb-actions__chips');
      this._scale = 1;
      this._tyEndVal = END_TOP_BASE;

      this._onScroll = this._tick.bind(this);
      this._onResize = this._sync.bind(this);
      this._mq = window.matchMedia('(min-width: ' + LG + 'px)');
      this._rm = window.matchMedia('(prefers-reduced-motion: reduce)');
      this._onMq = this._sync.bind(this);

      if (this._mq.addEventListener) {
        this._mq.addEventListener('change', this._onMq);
      } else {
        this._mq.addListener(this._onMq);
      }
      if (this._rm.addEventListener) {
        this._rm.addEventListener('change', this._onMq);
      } else {
        this._rm.addListener(this._onMq);
      }

      window.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize, { passive: true });

      this._ro = new ResizeObserver(this._sync.bind(this));
      var bar = document.querySelector('[data-lp-kv-bar]');
      if (bar) this._ro.observe(bar);
      if (this._stage) this._ro.observe(this._stage);
      if (this._intro) this._ro.observe(this._intro);
      if (this._chips) this._ro.observe(this._chips);

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this._sync.bind(this));
      }

      this._sync();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      window.removeEventListener('resize', this._onResize);
      if (this._mq) {
        if (this._mq.removeEventListener) {
          this._mq.removeEventListener('change', this._onMq);
        } else {
          this._mq.removeListener(this._onMq);
        }
      }
      if (this._rm) {
        if (this._rm.removeEventListener) {
          this._rm.removeEventListener('change', this._onMq);
        } else {
          this._rm.removeListener(this._onMq);
        }
      }
      if (this._ro) this._ro.disconnect();
      this._clearAnim();
    }

    _tyEnd() {
      return this._tyEndVal;
    }

    _fixedTail() {
      var introH = this._intro ? this._intro.offsetHeight : 0;
      var chipsH = this._chips ? this._chips.offsetHeight : 0;
      return introH + chipsH + BOTTOM_PAD;
    }

    _syncTop() {
      var top = this._mq && this._mq.matches ? kvBarH() : 0;
      var available = window.innerHeight - top;
      if (this._stage && this.classList.contains('is-pin')) {
        var stageH = this._stage.clientHeight;
        if (stageH > 0) available = Math.min(available, stageH);
      }

      var scale = 1;
      if (this._mq && this._mq.matches) {
        var room = available - this._fixedTail();
        scale = SCALED_PART > 0 ? Math.min(1, Math.max(0, room / SCALED_PART)) : 1;
      }

      var productH = PRODUCT_BASE * scale;
      var startTop = (available - productH) / 2;
      var endTop = END_TOP_BASE * scale;
      var tyEnd = endTop - startTop;

      this._scale = scale;
      this._tyEndVal = tyEnd;
      this.style.setProperty('--kb-actions-top', top + 'px');
      this.style.setProperty('--kb-actions-scale', String(scale));
      this.style.setProperty('--kb-actions-product-top', startTop + 'px');
      this.style.setProperty('--kb-actions-ty-end', tyEnd + 'px');
    }

    _clearAnim() {
      if (this._product) {
        this._product.style.transform = '';
      }
      if (this._reveal) {
        this._reveal.style.opacity = '';
      }
    }

    _applyEnd() {
      if (this._product) {
        this._product.style.transform = 'translate3d(0, ' + this._tyEnd() + 'px, 0)';
      }
      if (this._reveal) {
        this._reveal.style.opacity = '1';
      }
    }

    _progress() {
      if (!this._track) return 0;
      var rect = this._track.getBoundingClientRect();
      var trackH = this._track.offsetHeight;
      var vh = window.innerHeight;
      var scrollable = trackH - vh;
      if (scrollable <= 0) return 1;
      var scrolled = -rect.top;
      return clamp(scrolled / scrollable, 0, 1);
    }

    _tick() {
      if (!(this._mq && this._mq.matches) || (this._rm && this._rm.matches)) return;
      this._syncTop();

      var p = this._progress();
      /* Hold start + end frames; scrub only in the middle of the track */
      var DWELL_START = 0.15;
      var DWELL_END = 0.15;
      var animSpan = 1 - DWELL_START - DWELL_END;
      var tyT = clamp((p - DWELL_START) / animSpan, 0, 1);
      var ty = lerp(0, this._tyEnd(), tyT);

      var op;
      if (p < DWELL_START) {
        op = 0;
      } else {
        op = easeMid(clamp((p - DWELL_START) / animSpan, 0, 1));
      }

      if (this._product) {
        this._product.style.transform = 'translate3d(0, ' + ty + 'px, 0)';
      }
      if (this._reveal) {
        this._reveal.style.opacity = String(op);
      }
    }

    _sync() {
      var pin = this._mq && this._mq.matches;
      this.classList.toggle('is-pin', pin);
      this.classList.toggle('is-flow', !pin);
      this._syncTop();

      if (!pin) {
        this._clearAnim();
        return;
      }

      if (this._rm && this._rm.matches) {
        this._applyEnd();
        return;
      }

      this._tick();
    }
  }

  if (!customElements.get('keyboard-actions')) {
    customElements.define('keyboard-actions', KeyboardActions);
  }
})();
